import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import EmptyState from '../components/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { useConfirm } from '../context/ConfirmContext';
import './Tasks.css';
import './WorkGroups.css';

const WorkGroups = () => {
  const { t } = useTranslation(['workGroups', 'common']);
  const confirm = useConfirm();
  const { user } = useContext(AuthContext);
  const [workGroups, setWorkGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [busyId, setBusyId] = useState(null);

  const companyId = user?.active_company_id || user?.company_id;

  useEffect(() => {
    fetchWorkGroups();
  }, [companyId]);

  const fetchWorkGroups = async ({ quiet = false } = {}) => {
    try {
      if (!quiet) {
        setLoading(true);
      }
      setError('');
      const response = await axios.get('/api/work-groups');
      setWorkGroups(response.data.data || []);
    } catch (err) {
      console.error('Error fetching work groups:', err);
      setError(formatError(err) || t('workGroups:failedFetch'));
      setWorkGroups([]);
    } finally {
      if (!quiet) {
        setLoading(false);
      }
    }
  };

  const handleOpenCreate = () => {
    setCreateName('');
    setError('');
    setShowCreateModal(true);
  };

  const handleCloseCreate = () => {
    setShowCreateModal(false);
    setCreateName('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const trimmed = createName.trim();
    if (!trimmed || submitting) {
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await axios.post('/api/work-groups', { name: trimmed });
      handleCloseCreate();
      await fetchWorkGroups({ quiet: true });
    } catch (err) {
      setError(formatError(err) || t('workGroups:failedCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRename = async (id) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      return;
    }
    setBusyId(id);
    setError('');
    try {
      await axios.patch(`/api/work-groups/${id}`, { name: trimmed });
      setEditingId(null);
      setEditName('');
      await fetchWorkGroups({ quiet: true });
    } catch (err) {
      setError(formatError(err) || t('workGroups:failedRename'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm(t('workGroups:confirmDelete'), { danger: true }))) {
      return;
    }
    setBusyId(id);
    setError('');
    try {
      await axios.delete(`/api/work-groups/${id}`);
      if (editingId === id) {
        setEditingId(null);
        setEditName('');
      }
      await fetchWorkGroups({ quiet: true });
    } catch (err) {
      setError(formatError(err) || t('workGroups:failedDelete'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="work-groups-page">
      <div className="container container--wide main-content">
        <div className="page-header">
          <h1>{t('workGroups:pageTitle')}</h1>
          <button
            type="button"
            className="btn btn-primary tasks-header-create-btn"
            onClick={handleOpenCreate}
          >
            <span className="material-symbols-outlined" aria-hidden="true">add</span>
            {t('workGroups:create')}
          </button>
        </div>

        {error && !showCreateModal && <div className="error">{error}</div>}

        {workGroups.length > 0 ? (
          <section className="tasks-section">
            <div className="tasks-list-table-wrap tasks-list-table-desktop">
              <table className="table tasks-list-table">
                <thead>
                  <tr>
                    <th>{t('common:name')}</th>
                    <th>{t('workGroups:columnTasks')}</th>
                    <th>{t('common:actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {workGroups.map((group) => (
                    <tr key={group.id} className="tasks-list-table-row">
                      <td className="tasks-list-table-title">
                        {editingId === group.id ? (
                          <div className="work-groups-edit-row">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              maxLength={255}
                              disabled={busyId === group.id}
                              aria-label={t('workGroups:rename')}
                            />
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => handleRename(group.id)}
                              disabled={busyId === group.id || !editName.trim()}
                            >
                              {t('common:save')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setEditingId(null);
                                setEditName('');
                              }}
                              disabled={busyId === group.id}
                            >
                              {t('common:cancel')}
                            </button>
                          </div>
                        ) : (
                          group.name
                        )}
                      </td>
                      <td>{group.task_count != null ? group.task_count : '—'}</td>
                      <td className="work-groups-actions-cell">
                        {editingId !== group.id && (
                          <>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setEditingId(group.id);
                                setEditName(group.name);
                              }}
                              disabled={busyId != null}
                            >
                              {t('workGroups:rename')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDelete(group.id)}
                              disabled={busyId != null}
                            >
                              {t('workGroups:delete')}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="tasks-card-grid tasks-list-cards-mobile">
              {workGroups.map((group) => (
                <article key={group.id} className="tasks-redesign-card">
                  <div className="tasks-redesign-card-body">
                    {editingId === group.id ? (
                      <div className="work-groups-edit-row">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          maxLength={255}
                          disabled={busyId === group.id}
                          aria-label={t('workGroups:rename')}
                        />
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => handleRename(group.id)}
                          disabled={busyId === group.id || !editName.trim()}
                        >
                          {t('common:save')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            setEditingId(null);
                            setEditName('');
                          }}
                          disabled={busyId === group.id}
                        >
                          {t('common:cancel')}
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="tasks-redesign-card-top">
                          <h3 className="tasks-redesign-card-title">{group.name}</h3>
                        </div>
                        <div className="tasks-redesign-card-meta">
                          <span className="tasks-redesign-meta-item">
                            <span className="material-symbols-outlined" aria-hidden="true">assignment</span>
                            {t('workGroups:taskCount', { count: Number(group.task_count) || 0 })}
                          </span>
                        </div>
                        <div className="work-groups-card-actions">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              setEditingId(group.id);
                              setEditName(group.name);
                            }}
                            disabled={busyId != null}
                          >
                            {t('workGroups:rename')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(group.id)}
                            disabled={busyId != null}
                          >
                            {t('workGroups:delete')}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : (
          <EmptyState
            icon="group_work"
            title={t('workGroups:emptyTitle')}
            description={t('workGroups:emptyDescription')}
            actionLabel={t('workGroups:create')}
            onAction={handleOpenCreate}
          />
        )}

        <Modal
          isOpen={showCreateModal}
          onClose={handleCloseCreate}
          title={t('workGroups:createTitle')}
          className="work-groups-create-modal"
        >
          {error && showCreateModal && <div className="error">{error}</div>}
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label htmlFor="work-group-name">{t('common:name')}</label>
              <input
                id="work-group-name"
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                maxLength={255}
                required
                autoFocus
                disabled={submitting}
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseCreate}
                disabled={submitting}
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || !createName.trim()}
              >
                {submitting ? t('common:saving') : t('workGroups:create')}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default WorkGroups;
